<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import FileUpload from 'vue-upload-component';
import AttachmentsPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import {
  ALLOWED_FILE_TYPES,
  MAXIMUM_FILE_UPLOAD_SIZE,
} from 'shared/constants/messages';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    FileUpload,
    AttachmentsPreview,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    existingAttachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      show: true,
      newFiles: [],
      existingFiles: [],
      removeAttachmentIds: [],
      ALLOWED_FILE_TYPES,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    allAttachments() {
      return [...this.existingFiles, ...this.newFiles];
    },
  },
  mounted() {
    this.existingFiles = this.existingAttachments.map(att => ({
      id: att.id,
      resource: {
        filename: att.filename,
        name: att.filename,
        type: att.file_type,
        byte_size: att.file_size,
      },
      thumb: att.file_type?.includes('image') ? att.file_url : null,
      isExisting: true,
    }));
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.newFiles = [];
      this.existingFiles = [];
      this.removeAttachmentIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onFileUpload(file) {
      if (!checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
        useAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: MAXIMUM_FILE_UPLOAD_SIZE,
          })
        );
        return;
      }
      const reader = new FileReader();
      reader.onload = e => {
        this.newFiles.push({
          resource: file.file,
          thumb: file.file.type.includes('image') ? e.target.result : null,
        });
      };
      reader.readAsDataURL(file.file);
    },
    removeAttachment(updatedAttachments) {
      const removedItems = this.allAttachments.filter(
        att => !updatedAttachments.includes(att)
      );
      removedItems.forEach(item => {
        if (item.isExisting && item.id) {
          this.removeAttachmentIds.push(item.id);
          this.existingFiles = this.existingFiles.filter(f => f.id !== item.id);
        } else {
          this.newFiles = this.newFiles.filter(f => f !== item);
        }
      });
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      const files = this.newFiles.map(a => a.resource);
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          files,
          removeAttachmentIds: this.removeAttachmentIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <div class="w-full mt-4">
          <label>{{ $t('CANNED_MGMT.EDIT.FORM.ATTACHMENTS.LABEL') }}</label>
          <div class="flex flex-col gap-2">
            <AttachmentsPreview
              v-if="allAttachments.length"
              :attachments="allAttachments"
              @remove-attachment="removeAttachment"
            />
            <FileUpload
              :size="4096 * 4096"
              :accept="ALLOWED_FILE_TYPES"
              multiple
              @input-file="onFileUpload"
            >
              <NextButton
                icon="i-lucide-paperclip"
                faded
                slate
                sm
                :label="$t('CANNED_MGMT.EDIT.FORM.ATTACHMENTS.BUTTON')"
              />
            </FileUpload>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
